import { PropsWithChildren, useEffect, useRef, useState } from 'react'

import { ISSUE_RESPONSIBILITY, IssueCases, OtherCases } from '@img-arena/consoles-api-streaming'
import { Grid } from '@mui/material'
import { ConsoleTextField } from '@/components/inputs/StyledInputComponents'

import { CaseCheckbox } from './CaseCheckbox'
import { CASES_BY_SCOPE, GENERIC_INPUT_PLACEHOLDER } from './constants'
import { EditableIssue, NormalCase, Scope } from './types'
import { isOtherCase } from './utils'

const GridContainer = ({ children }: PropsWithChildren) => (
  <Grid container spacing={2} rowSpacing={1} size={12}>
    {children}
  </Grid>
)

type Props = {
  scope: Scope
  cases?: EditableIssue['cases']
  onChange: (cases: IssueCases) => void
}

export const CasesByScope = ({ scope, cases, onChange }: Props) => {
  const [internalCases, setInternalCases] = useState<IssueCases | null>(null)
  const [checkedCases, setCheckedCases] = useState<Record<string, boolean>>({})
  const previousValueRef = useRef<IssueCases | null>(null)
  const isInitializingRef = useRef(true)

  useEffect(() => {
    if (!internalCases) {
      if (previousValueRef.current !== null) {
        previousValueRef.current = null
        onChange(null)
      }
      return
    }

    const filteredCases = Object.entries(internalCases).reduce(
      (prev, [key, value]) => {
        if (isOtherCase(value)) {
          if (
            value.description &&
            value.description.trim() &&
            value.duration &&
            value.duration.trim()
          ) {
            return { ...prev, [key]: value }
          }
          return prev
        }

        if (checkedCases[key]) {
          return {
            ...prev,
            [key]: value
          }
        }
        return prev
      },
      {} as Record<string, { duration: string }>
    )

    const newValue = Object.keys(filteredCases).length > 0 ? (filteredCases as IssueCases) : null

    if (isInitializingRef.current) {
      const casesValue = cases && Object.keys(cases).length > 0 ? cases : null
      if (JSON.stringify(newValue) === JSON.stringify(casesValue)) {
        previousValueRef.current = newValue
        isInitializingRef.current = false
        return
      }
      isInitializingRef.current = false
    }

    if (JSON.stringify(previousValueRef.current) === JSON.stringify(newValue)) {
      return
    }

    previousValueRef.current = newValue
    onChange(newValue)
  }, [internalCases, checkedCases, cases])

  useEffect(() => {
    if (!scope) return

    isInitializingRef.current = true

    if (!cases) {
      setInternalCases(CASES_BY_SCOPE[scope])
      setCheckedCases({})
      return
    }

    const mergedCases = {
      ...CASES_BY_SCOPE[scope],
      ...cases
    }

    setInternalCases(mergedCases)

    const initialCheckedCases: Record<string, boolean> = {}
    Object.keys(cases).forEach((key) => {
      initialCheckedCases[key] = true
    })

    setCheckedCases(initialCheckedCases)
  }, [scope, cases])

  if (!internalCases) return

  const handleCaseChange = (
    name: string,
    value: {
      duration: string
      description?: string
    }
  ) => {
    const currentCase = internalCases[name as keyof typeof internalCases] as NormalCase

    if (scope === ISSUE_RESPONSIBILITY.OTHER && isOtherCase(currentCase)) {
      const updatedCases = {
        ...internalCases,
        [name]: {
          ...currentCase,
          ...value
        }
      }

      setInternalCases(updatedCases)
      return
    }

    const updatedCases = {
      ...internalCases,
      [name]: {
        ...currentCase,
        ...value
      }
    }

    setInternalCases(updatedCases)
  }

  if (scope === ISSUE_RESPONSIBILITY.OTHER) {
    const { broadcastOther } = internalCases as OtherCases

    if (!broadcastOther) return

    return (
      <GridContainer>
        <Grid size={4}>
          <ConsoleTextField
            placeholder="Short Description"
            name="broadcastOther-description"
            value={broadcastOther.description}
            label="Issues Description"
            required
            fullWidth
            onChange={(e) =>
              handleCaseChange('broadcastOther', { ...broadcastOther, description: e.target.value })
            }
          />
        </Grid>
        <Grid size={4}>
          <ConsoleTextField
            placeholder={GENERIC_INPUT_PLACEHOLDER}
            name="broadcastOther-duration"
            value={broadcastOther.duration}
            label="Duration/Extent"
            required
            fullWidth
            onChange={(e) =>
              handleCaseChange('broadcastOther', { ...broadcastOther, duration: e.target.value })
            }
          />
        </Grid>
      </GridContainer>
    )
  }

  const handleCheckboxChange = (attribute: string, checked: boolean) => {
    setCheckedCases((prev) => ({
      ...prev,
      [attribute]: checked
    }))
  }

  return (
    <GridContainer>
      {Object.entries(internalCases).map(([attribute, value]) => (
        <CaseCheckbox
          checked={checkedCases[attribute]}
          key={attribute}
          attribute={attribute}
          value={value.duration}
          onChange={(newValue) => handleCaseChange(attribute, newValue)}
          onCheckboxChange={(checked) => handleCheckboxChange(attribute, checked)}
        />
      ))}
    </GridContainer>
  )
}
